import { Business as BusinessIcon } from "@mui/icons-material";
import { Box, Grid, IconButton, Menu, MenuItem, Stack, TextField, Typography } from "@mui/material";
import sum from "lodash/sum";
import type { FC } from "react";
import type { MouseEvent } from "react";
import { Fragment, useState } from "react";
import NoteStampedInput from "../../components/noteStampedList/NoteStampedInput";
import type { NoteTS } from "../../components/noteStampedList/NoteStampedList";
import { NoteStampedList } from "../../components/noteStampedList/NoteStampedList";
import type { Currency } from "../../data/BaseEntity";
import dayjs, { DATE_FORMAT, ISO_DATE_FORMAT } from "../../dayjs";
import { auth } from "../../auth/auth";
import { Price } from "../../misc/price";
import type { OrderPayment } from "../orderTypes";
import MoneyValue, { moneyText } from "./MoneyValue";
import type { InvoiceSellerId, OrderPriceByDepartmentResponse, PaymentDTO } from "../ordersApi";
import { useOrderPriceByDepartment } from "../invoices/useOrderPriceByDepartment";

const COMPANY_LABELS: Record<InvoiceSellerId, string> = {
  PRZEZMORZE: "Przezmorze",
  B2B_HOTELS: "B2BHotels",
};

const COMPANIES = Object.keys(COMPANY_LABELS) as InvoiceSellerId[];

const UNASSIGNED_PAYMENT_LABEL = "Nieprzypisana";
const UNASSIGNED_PAYMENTS_LABEL = "Nieprzypisane";
const PAYMENTS_SUM_LABEL = "Suma wpłat";
const TO_BE_PAID_LABEL = "Do zapłaty";

interface CompanyAmounts {
  readonly label: string;
  readonly paid: number;
  readonly toBePaid: number | null;
}

export function sumPayments(payments: OrderPayment[]) {
  const paymentValues = (payments ?? []).map((p) => Number(p.value));
  return sum(paymentValues);
}

function toNoteTSNumber(payment: PaymentDTO): NoteTS {
  const companyLabel = payment.company ? COMPANY_LABELS[payment.company] : UNASSIGNED_PAYMENT_LABEL;
  return {
    author: payment.reason ? `Wpłata przypisana automatycznie:\n${payment.reason}` : payment.author,
    date: dayjs(payment.date, ISO_DATE_FORMAT),
    text: `${companyLabel}: ${payment.reason ? "🖧 " : ""}${payment.value}`,
  };
}

function companyAmounts(
  payments: PaymentDTO[],
  priceBreakdown: OrderPriceByDepartmentResponse | undefined,
): CompanyAmounts[] {
  const companyRows = COMPANIES.filter(
    (company) =>
      payments.some((payment) => payment.company === company) || (priceBreakdown?.proforma[company]?.amount ?? 0) > 0,
  ).map((company) => {
    const paid = sumPayments(payments.filter((payment) => payment.company === company));
    const toBePaid = priceBreakdown
      ? (new Price(priceBreakdown.proforma[company]?.amount ?? 0).subtract(paid).value as number)
      : null;
    return { label: COMPANY_LABELS[company], paid, toBePaid };
  });
  const unassignedPayments = payments.filter((payment) => !payment.company);
  return unassignedPayments.length > 0
    ? [...companyRows, { label: UNASSIGNED_PAYMENTS_LABEL, paid: sumPayments(unassignedPayments), toBePaid: null }]
    : companyRows;
}

interface CompanyMoneyValueProps {
  readonly columnLabel: string;
  readonly companyLabel: string;
  readonly value: number;
  readonly currency: Currency;
}

const CompanyMoneyValue: FC<CompanyMoneyValueProps> = ({ columnLabel, companyLabel, value, currency }) => (
  <Typography variant="body1" aria-label={`${columnLabel} ${companyLabel}`}>
    {moneyText(value, currency, companyLabel)}
  </Typography>
);

interface AssignCompanyButtonProps {
  readonly company: InvoiceSellerId | null | undefined;
  readonly onAssign: (company: InvoiceSellerId) => Promise<void>;
}

const AssignCompanyButton: FC<AssignCompanyButtonProps> = ({ company, onAssign }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const handleAssign = async (event: MouseEvent, selectedCompany: InvoiceSellerId) => {
    event.stopPropagation();
    setAnchor(null);
    if (selectedCompany !== company) {
      await onAssign(selectedCompany);
    }
  };

  return (
    <>
      <IconButton
        title="Przypisz firmę"
        onClick={(event) => {
          event.stopPropagation();
          setAnchor(event.currentTarget);
        }}
      >
        <BusinessIcon />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        {COMPANIES.map((companyOption) => (
          <MenuItem
            key={companyOption}
            selected={companyOption === company}
            onClick={(event) => handleAssign(event, companyOption)}
          >
            {COMPANY_LABELS[companyOption]}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

interface Props {
  readonly orderId: string | undefined;
  readonly toBePaid: Price;
  readonly payments: PaymentDTO[];
  readonly currency: Currency;
  readonly readonly?: boolean;
  readonly onUpdatePayments?: (payments: PaymentDTO[]) => Promise<void>;
}

const OrderPayments: FC<Props> = ({ orderId, currency, toBePaid, payments, readonly, onUpdatePayments }) => {
  const [company, setCompany] = useState<InvoiceSellerId | null>(null);
  const { data: priceBreakdown } = useOrderPriceByDepartment(orderId);

  const handleAdd = async (value: string, date: string | null) => {
    if (!company) {
      return;
    }
    const updatedPayments = [
      ...payments,
      {
        value: Number(value),
        date: date ?? "",
        author: auth.currentUser?.email ?? "",
        company,
      },
    ];
    await onUpdatePayments!(updatedPayments);
  };

  const handleDelete = async (idx: number, payment: NoteTS) => {
    if (confirm(`Usunąć wpłatę ${payment.text} z ${payment.date.format(DATE_FORMAT)}?`)) {
      const updatedPayments = payments.filter((n, i) => i !== idx);
      await onUpdatePayments!(updatedPayments);
    }
  };

  const handleAssignCompany = async (idx: number, assignedCompany: InvoiceSellerId) => {
    const updatedPayments = payments.map((payment, i) =>
      i === idx ? { ...payment, company: assignedCompany } : payment,
    );
    await onUpdatePayments!(updatedPayments);
  };

  const paymentsSum = sumPayments(payments);
  return (
    <Grid
      container
      sx={{
        rowGap: 1,
      }}
    >
      <Grid size={12}>
        <Typography
          variant="h6"
          sx={{
            marginTop: "8px",
          }}
        >
          Wpłaty
        </Typography>
        <div>
          <NoteStampedList
            values={payments.map(toNoteTSNumber)}
            onRemove={readonly ? undefined : handleDelete}
            renderActions={
              readonly
                ? undefined
                : (idx) => (
                    <AssignCompanyButton
                      company={payments[idx].company}
                      onAssign={(assignedCompany) => handleAssignCompany(idx, assignedCompany)}
                    />
                  )
            }
          />
          <Stack direction="row" spacing={1}>
            <TextField
              select
              label="Firma"
              value={company ?? ""}
              onChange={(e) => setCompany(e.target.value as InvoiceSellerId)}
              disabled={readonly}
              sx={{ minWidth: 160 }}
            >
              {COMPANIES.map((companyOption) => (
                <MenuItem key={companyOption} value={companyOption}>
                  {COMPANY_LABELS[companyOption]}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ flexGrow: 1 }}>
              <NoteStampedInput
                date={dayjs().format(ISO_DATE_FORMAT)}
                type="number"
                label="Wpłata"
                dateLabel="Data wpłaty"
                readonly={readonly || !company}
                onSubmit={handleAdd}
              />
            </Box>
          </Stack>
        </div>
      </Grid>
      <Grid size={6}>
        <MoneyValue label={PAYMENTS_SUM_LABEL} value={paymentsSum} currency={currency} />
      </Grid>
      <Grid size={6}>
        <MoneyValue
          label={TO_BE_PAID_LABEL}
          value={toBePaid.isPresent() ? (toBePaid.subtract(paymentsSum).value as number) : null}
          currency={currency}
        />
      </Grid>
      {companyAmounts(payments, priceBreakdown).map(({ label, paid, toBePaid: companyToBePaid }) => (
        <Fragment key={label}>
          <Grid size={6}>
            <CompanyMoneyValue columnLabel={PAYMENTS_SUM_LABEL} companyLabel={label} value={paid} currency={currency} />
          </Grid>
          <Grid size={6}>
            {companyToBePaid !== null && (
              <CompanyMoneyValue
                columnLabel={TO_BE_PAID_LABEL}
                companyLabel={label}
                value={companyToBePaid}
                currency={currency}
              />
            )}
          </Grid>
        </Fragment>
      ))}
    </Grid>
  );
};

export default OrderPayments;
